'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import type { TooltipProps } from 'recharts';

const ResponsiveContainer = dynamic(
  () => import('recharts').then((m) => m.ResponsiveContainer),
  { ssr: false }
);
const AreaChart = dynamic(
  () => import('recharts').then((m) => m.AreaChart),
  { ssr: false }
);
const Area = dynamic(
  () => import('recharts').then((m) => m.Area),
  { ssr: false }
);
const XAxis = dynamic(
  () => import('recharts').then((m) => m.XAxis),
  { ssr: false }
);
const YAxis = dynamic(
  () => import('recharts').then((m) => m.YAxis),
  { ssr: false }
);
const CartesianGrid = dynamic(
  () => import('recharts').then((m) => m.CartesianGrid),
  { ssr: false }
);
const Tooltip = dynamic(
  () => import('recharts').then((m) => m.Tooltip),
  { ssr: false }
);
const ReferenceLine = dynamic(
  () => import('recharts').then((m) => m.ReferenceLine),
  { ssr: false }
);
const Defs = dynamic(
  () => import('recharts').then((m) => m.Defs),
  { ssr: false }
);
const LinearGradient = dynamic(
  () => import('recharts').then((m) => m.LinearGradient),
  { ssr: false }
);
const Stop = dynamic(
  () => import('recharts').then((m) => m.Stop),
  { ssr: false }
);

const equityCurveData = [
  { date: 'Aug 1', pnl: 0, cumulative: 0 },
  { date: 'Aug 3', pnl: 820, cumulative: 820 },
  { date: 'Aug 4', pnl: -340, cumulative: 480 },
  { date: 'Aug 5', pnl: 1240, cumulative: 1720 },
  { date: 'Aug 6', pnl: 680, cumulative: 2400 },
  { date: 'Aug 7', pnl: -290, cumulative: 2110 },
  { date: 'Aug 8', pnl: 950, cumulative: 3060 },
  { date: 'Aug 11', pnl: 1380, cumulative: 4440 },
  { date: 'Aug 12', pnl: -1100, cumulative: 3340 },
  { date: 'Aug 13', pnl: -890, cumulative: 2450 },
  { date: 'Aug 14', pnl: -1220, cumulative: 1230 },
  { date: 'Aug 15', pnl: 760, cumulative: 1990 },
  { date: 'Aug 18', pnl: 1840, cumulative: 3830 },
  { date: 'Aug 19', pnl: 2100, cumulative: 5930 },
  { date: 'Aug 20', pnl: 1560, cumulative: 7490 },
  { date: 'Aug 21', pnl: -480, cumulative: 7010 },
  { date: 'Aug 22', pnl: 2340, cumulative: 9350 },
  { date: 'Aug 25', pnl: 5032, cumulative: 14382 },
];

function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0]?.value as number;
  const isPositive = val >= 0;
  return (
    <div className="chart-tooltip-card">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-sm font-600 font-mono-nums ${isPositive ? 'text-positive' : 'text-negative'}`}>
        {isPositive ? '+' : ''}${val.toLocaleString('en-US')}
      </p>
    </div>
  );
}

export default function EquityCurveChart() {
  return (
    <div className="bg-card border border-border rounded-xl p-5 card-glow-primary h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-600 text-foreground">Equity Curve</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Cumulative P&L — August 2026</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-primary inline-block" />
            Cumulative P&L
          </span>
        </div>
      </div>

      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={equityCurveData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.25} />
                <stop offset="95%" stopColor="var(--primary)" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              interval={2}
            />
            <YAxis
              tick={{ fill: 'var(--muted-foreground)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              width={45}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="var(--border)" strokeDasharray="4 4" />
            <Area
              type="monotone"
              dataKey="cumulative"
              stroke="var(--primary)"
              strokeWidth={2}
              fill="url(#equityGradient)"
              dot={false}
              activeDot={{ r: 4, fill: 'var(--primary)', stroke: 'var(--background)', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}